import { create } from 'zustand';
import { InterviewQuestions } from '@/types';
import { isInterviewQuestionsEmpty } from '@/utils/questionGroupHelpers';

export interface RequiredField {
  key: string;
  label: string;
  isEmpty: boolean;
}

type State = {
  requiredFields: RequiredField[];
  emptyFieldsLabels: string[];
};

type Action = {
  setRequiredFields: (fields: RequiredField[]) => void;
};

// The publish-required fields and their i18n labels. Single source of truth for
// both the live (form) computation on the Design page and the hydration from
// saved project data on every other route (TELV1-509).
const REQUIRED_FIELD_DEFS: { key: string; label: string }[] = [
  { key: 'interviewer_name', label: 'interviewerName' },
  { key: 'company_name', label: 'companyName' },
  { key: 'interview_goal', label: 'myResearchObjective' },
  { key: 'interview_audience', label: 'interviewAudience' },
  { key: 'language', label: 'primaryLanguage' },
  {
    key: 'allow_language_selection',
    label: 'allowParticipantsToChooseTheLanguage',
  },
  { key: 'analysis_language', label: 'analysisLanguage' },
  { key: 'privacy_policy', label: 'privacyPolicy' },
  { key: 'tone_of_voice', label: 'toneOfVoice' },
  { key: 'opening_message', label: 'openingMessage' },
  { key: 'interview_questions', label: 'interviewQuestions' },
  { key: 'respondents_max', label: 'maximumNumberOfRespondents' },
];

/**
 * Computes the required-field empty state from a project-like source — either
 * the live RHF form values (Design page) or the saved project (`singleProjectData`,
 * every route). Pure so both producers agree and the publish gate can never
 * depend on which page happened to mount (TELV1-509).
 */
export const computeRequiredFields = (
  source: Record<string, unknown> | undefined | null,
): RequiredField[] =>
  REQUIRED_FIELD_DEFS.map(({ key, label }) => {
    const value = source?.[key];

    let isEmpty: boolean;
    if (key === 'allow_language_selection') {
      // `false` ("No") and `true` ("Yes") are real selections; only an
      // absent value (null/undefined) is empty.
      isEmpty = value === null || value === undefined;
    } else if (key === 'interview_questions') {
      isEmpty = isInterviewQuestionsEmpty(value as InterviewQuestions | undefined);
    } else {
      isEmpty = !value;
    }

    return { key, label, isEmpty };
  });

// With no source, every field is empty — the pre-hydration default.
const initialRequiredFields: RequiredField[] = computeRequiredFields(undefined);

const useProjectRequiredFieldsStore = create<State & Action>((set) => ({
  requiredFields: initialRequiredFields,
  emptyFieldsLabels: initialRequiredFields
    .filter((field) => field.isEmpty)
    .map((field) => field.label),

  setRequiredFields: (fields) => {
    set({ requiredFields: fields });
    const emptyFields = fields.filter((field) => field.isEmpty).map((field) => field.label);
    set({ emptyFieldsLabels: emptyFields });
  },
}));

export default useProjectRequiredFieldsStore;
